import { usePwaContext } from "../hooks/use-pwa-context.js";
import { usePwaFlags } from "../hooks/use-pwa-flags.js";
import { useSaveContact } from "../hooks/use-save-contact.js";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Body1, Box, Button, Cover, Flex, Heading1, Modal, Stack, TextButton, useNavBar } from "@myphx-engineering/phx-design-system";
import { useHotkeys, useIsMobile } from "@myphx-engineering/phx-design-system/hooks";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/components/AddContactOverlay.tsx
const AddContactOverlay = ({ dismiss, visible }) => {
	const { setBackOverrideState } = useNavBar();
	const location = useLocation();
	const { appName, pwaId, pwaAssets } = usePwaContext();
	const saveContact = useSaveContact({
		downloadName: pwaAssets?.contactCard?.name ?? "",
		downloadUrl: pwaAssets?.contactCard?.url ?? ""
	});
	const isMobile = useIsMobile();
	const [showModal, setShowModal] = useState(false);
	const { t } = useTranslation("pwa");
	const { flags, setFlag } = usePwaFlags(pwaId);
	const closeModal = useCallback(() => {
		setFlag("addToContacts", true);
		setShowModal(false);
		dismiss();
	}, [
		dismiss,
		setFlag,
		setShowModal
	]);
	const closeTip = useCallback(() => {
		if (!visible) return;
		if (flags.addToContacts) dismiss();
		else setShowModal(true);
	}, [
		dismiss,
		flags.addToContacts,
		setShowModal,
		visible
	]);
	useHotkeys([["Escape", closeTip]]);
	useEffect(() => {
		if (!visible) return;
		setBackOverrideState({ overrideFn: closeTip });
		return () => setBackOverrideState({ overrideFn: null });
	}, [
		closeTip,
		setBackOverrideState,
		visible
	]);
	useEffect(() => closeTip(), [closeTip, location.key]);
	return /* @__PURE__ */ jsxs(Cover, {
		visible,
		children: [/* @__PURE__ */ jsxs(Stack, {
			align: "center",
			gap: "lg",
			p: "sm",
			w: "100%",
			children: [
				/* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx("img", {
					src: pwaAssets.illustrations?.addToContacts,
					alt: t("addContact.illustrations.addContact"),
					width: "200",
					height: "200"
				}) }),
				/* @__PURE__ */ jsxs(Stack, {
					gap: "sm",
					children: [/* @__PURE__ */ jsx(Heading1, {
						ta: "center",
						children: t("addContact.heading")
					}), /* @__PURE__ */ jsx(Body1, {
						ta: "center",
						children: t("addContact.instructions", { appName })
					})]
				}),
				/* @__PURE__ */ jsx(Button, {
					fullWidth: isMobile,
					variant: "contained",
					onClick: saveContact,
					"data-testid": "save-tip-button",
					children: t("addContact.saveButton")
				}),
				/* @__PURE__ */ jsxs(Flex, {
					gap: "xxxs",
					children: [/* @__PURE__ */ jsx(Body1, {
						inline: true,
						children: t("addContact.alreadyDone")
					}), /* @__PURE__ */ jsx(TextButton, {
						onClick: closeTip,
						"data-testid": "dismiss-tip-button",
						children: t("addContact.dismiss")
					})]
				})
			]
		}), /* @__PURE__ */ jsx(Modal, {
			opened: showModal,
			onClose: closeModal,
			title: t("addContact.modal.title", { appName }),
			children: /* @__PURE__ */ jsxs(Stack, {
				gap: "lg",
				children: [
					/* @__PURE__ */ jsx(Body1, { children: t("addContact.modal.content", { appName }) }),
					/* @__PURE__ */ jsx(Flex, {
						align: "center",
						justify: "center",
						children: /* @__PURE__ */ jsx("img", {
							src: pwaAssets.illustrations?.addToContactsFromMenu,
							alt: t("addContact.illustrations.addFromMenu"),
							width: "340",
							height: "180"
						})
					}),
					/* @__PURE__ */ jsx(Button, {
						onClick: closeModal,
						children: t("addContact.modal.done")
					})
				]
			})
		})]
	});
};

//#endregion
export { AddContactOverlay };